import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { fetchGeoCities, fetchGeoTowns, fetchGeoQuarters } from './api.js';

function dedupeByLabel(rows, getId, getLabel) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const id = getId(row);
    const label = String(getLabel(row) ?? '').trim();
    if (id == null || id === '' || !label) continue;
    const k = label.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ id: String(id), label });
  }
  return out;
}

function quarterPrimaryLabel(q) {
  const a = String(q.Tkgm_text ?? '').trim();
  if (a) return a;
  return String(q.Proparcel_text ?? '').trim();
}

function dedupeQuarters(rows) {
  const seen = new Set();
  const out = [];
  for (const q of rows) {
    const id = q.Id;
    const label = quarterPrimaryLabel(q);
    if (id == null || !label) continue;
    const k = label.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ id: String(id), label });
  }
  return out;
}

/**
 * Şehir / İlçe / Mahalle — Select2 (jQuery global, ilan_portal_vitrin_vite şablonunda yüklenir).
 */
export function VitrinLocationSelects({ cityId, districtId, quarterId, onLocationChange }) {
  const cityRef = useRef(null);
  const districtRef = useRef(null);
  const quarterRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [ready, setReady] = useState(false);

  const cityOptions = useMemo(
    () => dedupeByLabel(cities, (r) => r.Id, (r) => r.Proparcel_text),
    [cities],
  );
  const townOptions = useMemo(
    () => dedupeByLabel(towns, (r) => r.Id, (r) => r.Proparcel_text),
    [towns],
  );
  const quarterOptions = useMemo(() => dedupeQuarters(quarters), [quarters]);

  useEffect(() => {
    let cancelled = false;
    fetchGeoCities()
      .then((list) => {
        if (!cancelled) setCities(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cityId) {
      setTowns([]);
      return;
    }
    let cancelled = false;
    fetchGeoTowns(cityId)
      .then((list) => {
        if (!cancelled) setTowns(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setTowns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [cityId]);

  useEffect(() => {
    if (!districtId) {
      setQuarters([]);
      return;
    }
    let cancelled = false;
    fetchGeoQuarters(districtId)
      .then((list) => {
        if (!cancelled) setQuarters(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setQuarters([]);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (typeof window !== 'undefined' && window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
        setReady(true);
        window.clearInterval(t);
      }
    }, 30);
    return () => window.clearInterval(t);
  }, []);

  const bindSelect2 = useCallback(() => {
    const $ = window.jQuery;
    if (!$ || !$.fn.select2 || !ready) return;

    const $city = $(cityRef.current);
    const $dist = $(districtRef.current);
    const $qu = $(quarterRef.current);

    [$city, $dist, $qu].forEach(($el) => {
      if ($el && $el.length && $el.data('select2')) {
        $el.select2('destroy');
      }
    });

    const common = {
      width: '100%',
      allowClear: false,
      language: {
        noResults: () => 'Sonuç yok',
        searching: () => 'Aranıyor…',
      },
    };

    $city.select2({ ...common, placeholder: 'Şehir' });
    $city.off('.vloc');
    $city.val(cityId || '');
    $city.on('select2:select.vloc', () => {
      const v = String($city.val() || '');
      onLocationChange({ cityId: v, districtId: '', quarterId: '' });
    });

    $dist.select2({ ...common, placeholder: 'İlçe' });
    $dist.off('.vloc');
    $dist.val(districtId || '');
    $dist.on('select2:select.vloc', () => {
      const v = String($dist.val() || '');
      onLocationChange({
        cityId: cityId || '',
        districtId: v,
        quarterId: '',
      });
    });

    $qu.select2({ ...common, placeholder: 'Mahalle' });
    $qu.off('.vloc');
    $qu.val(quarterId || '');
    $qu.on('select2:select.vloc', () => {
      const v = String($qu.val() || '');
      onLocationChange({
        cityId: cityId || '',
        districtId: districtId || '',
        quarterId: v,
      });
    });
  }, [ready, cityId, districtId, quarterId, onLocationChange]);

  useLayoutEffect(() => {
    bindSelect2();
  }, [
    bindSelect2,
    cityOptions,
    townOptions,
    quarterOptions,
    ready,
  ]);

  return (
    <>
      <div class="pp-vitrin__filter-loc">
        <select ref={cityRef} class="pp-vitrin__loc-select" aria-label="Şehir">
          <option value="">Şehir</option>
          {cityOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      <div class="pp-vitrin__filter-loc">
        <select ref={districtRef} class="pp-vitrin__loc-select" aria-label="İlçe" disabled={!cityId}>
          <option value="">İlçe</option>
          {townOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div class="pp-vitrin__filter-loc">
        <select ref={quarterRef} class="pp-vitrin__loc-select" aria-label="Mahalle" disabled={!districtId}>
          <option value="">Mahalle</option>
          {quarterOptions.map((q) => (
            <option key={q.id} value={q.id}>
              {q.label}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
